import React, { useEffect, useState } from "react";

import api from "../../../api/api";
import GeneratedCard from "./GeneratedCard";

import {
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Grid,
  CircularProgress,
  Chip,
  TextField,
  TablePagination,
  Box,
  Divider,
  Avatar,
  Autocomplete,
  InputAdornment,
  Stack,
} from "@mui/material";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import AddCardIcon from "@mui/icons-material/AddCard";
import HistoryIcon from "@mui/icons-material/History";

import Swal from "sweetalert2";

const PrepaidCard = () => {

  const [customers, setCustomers] = useState([]);
const [previewCard, setPreviewCard] = useState(null);
  const [cards, setCards] = useState([]);

  const [loading, setLoading] = useState(false);

  // =========================
  // CARD CREATE STATES
  // =========================

  const [selectedCustomer, setSelectedCustomer] =
    useState(null);

  const [customerSearch, setCustomerSearch] =
    useState("");

  const [amount, setAmount] = useState("");

  // =========================
  // TABLE SEARCH
  // =========================

  const [search, setSearch] = useState("");

  // =========================
  // PAGINATION
  // =========================

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(4);

  const [totalCards, setTotalCards] =
    useState(0);

  // =========================
  // FETCH CUSTOMERS
  // =========================

  const fetchCustomers = async (
    searchText = ""
  ) => {

    try {

      const res = await api.get(
        `/card-holders/?search=${searchText}`
      );

      setCustomers(
        res.data.results || res.data
      );

    } catch (err) {

      Swal.fire(
        "Error",
        "Failed to load customers",
        "error"
      );
    }
  };

  // =========================
  // FETCH CARDS
  // =========================

  const fetchCards = async (
    pageNumber = 1,
    searchText = ""
  ) => {

    try {

      const res = await api.get(
        `/prepaid-cards/?page=${pageNumber}&page_size=${rowsPerPage}&search=${searchText}`
      );

      setCards(
        res.data.results || []
      );

      setTotalCards(
        res.data.count || 0
      );

    } catch (err) {

      Swal.fire(
        "Error",
        "Failed to load cards",
        "error"
      );
    }
  };

  // =========================
  // INITIAL LOAD
  // =========================

  useEffect(() => {

    fetchCustomers();

  }, []);

  // =========================
  // SEARCH DEBOUNCE
  // =========================

  useEffect(() => {

    const delay = setTimeout(() => {

      fetchCards(
        1,
        search
      );

    }, 500);

    return () => clearTimeout(delay);

  }, [search, rowsPerPage]);

  // =========================
  // CUSTOMER SEARCH DEBOUNCE
  // =========================

  useEffect(() => {

    const delay = setTimeout(() => {

      fetchCustomers(customerSearch);

    }, 500);

    return () => clearTimeout(delay);

  }, [customerSearch]);

  // =========================
  // CREATE CARD
  // =========================

const handleCreate = async () => {
    if (!selectedCustomer) {
      return Swal.fire("Warning", "Select a customer", "warning");
    }

    if (!amount || Number(amount) <= 0) {
      return Swal.fire("Warning", "Enter valid amount", "warning");
    }

    setLoading(true);

    try {
      const res = await api.post("/prepaid-cards/", {
        customer: selectedCustomer.id,
        balance: amount,
      });

      Swal.fire("Success", "Card created successfully", "success");

      setPreviewCard(res.data); // SHOW CARD DESIGN

      setSelectedCustomer(null);
      setAmount("");

      fetchCards(page + 1, search);

    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.customer || "Failed to create card",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // TOGGLE ACTIVE
  // =========================

  const toggleActive = async (
    card
  ) => {

    try {

      await api.patch(
        `/prepaid-cards/${card.id}/`,
        {
          is_active: !card.is_active,
        }
      );

      fetchCards(
        page + 1,
        search
      );

    } catch (err) {

      Swal.fire(
        "Error",
        "Failed to update card",
        "error"
      );
    }
  };

  // =========================
  // DEPOSIT
  // =========================

  const handleDeposit = async (
    card
  ) => {

    const { value: depositAmount } =
      await Swal.fire({

        title: "Deposit Funds",

        input: "number",

        inputLabel:
          `Deposit to ${card.account_number}`,

        inputPlaceholder:
          "Enter amount",

        confirmButtonColor: "#C9A227",

        showCancelButton: true,
      });

    if (!depositAmount) return;

    try {

      const res = await api.post(
        `/prepaid-cards/${card.id}/deposit/`,
        {
          amount: depositAmount,
        }
      );

      Swal.fire({
        icon: "success",
        title: "Deposit Successful",
        confirmButtonColor: "#C9A227",
        html: `
          <div style="text-align:left">
          
            <p>
              <b>Deposited:</b>
              $${Number(
                depositAmount
              ).toLocaleString()}
            </p>

            <p>
              <b>New Balance:</b>
              $${Number(
                res.data.new_balance
              ).toLocaleString()}
            </p>

          </div>
        `,
      });

      fetchCards(
        page + 1,
        search
      );

    } catch (err) {

      Swal.fire(
        "Error",
        "Deposit failed",
        "error"
      );
    }
  };

  // =========================
  // VIEW TRANSACTIONS
  // =========================

  const handleViewTransactions =
    async (card) => {

      try {

        const res = await api.get(
          `/prepaid-cards/${card.id}/transactions/`
        );

        const transactions =
          res.data.results || [];

        if (transactions.length === 0) {

          Swal.fire(
            "Info",
            "No transaction history found",
            "info"
          );

          return;
        }

        let html = `
          <div style="
            max-height:350px;
            overflow-y:auto;
            text-align:left;
          ">
        `;

        transactions.forEach((trx) => {

          html += `
            <div style="
              border-bottom:1px solid #eee;
              padding:10px 0;
            ">
            
              <p>
                <b>Type:</b>
                ${trx.transaction_type}
              </p>

              <p>
                <b>Amount:</b>
                $${Number(
                  trx.amount
                ).toLocaleString()}
              </p>

              <p>
                <b>Description:</b>
                ${trx.description}
              </p>

              <p>
                <b>Date:</b>
                ${new Date(
                  trx.created_at
                ).toLocaleString()}
              </p>

            </div>
          `;
        });

        html += `</div>`;

        Swal.fire({
          title: "Transaction History",
          html,
          width: 700,
        });

      } catch (err) {

        Swal.fire(
          "Error",
          "Failed to load transactions",
          "error"
        );
      }
    };

  return (

    <Box
      sx={{
        minHeight: "100vh",
        background: "#f4f7fb",
        p: { xs: 2, md: 4 },
      }}
    >

   

      {/* ========================= */}
      {/* HEADER */}
      {/* ========================= */}

      <Card
        sx={{
          mb: 4,
          p: 3,
          borderRadius: "24px",
          background: "#fff",
          border: "1px solid #e5e7eb",
          boxShadow:
            "0 4px 20px rgba(0,0,0,0.05)",
        }}
      >

        <Grid
          container
          spacing={3}
          alignItems="center"
        >

          <Grid item xs={12} md={8}>

            <Box
              display="flex"
              alignItems="center"
              gap={2}
            >

              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  background:
                    "linear-gradient(135deg,#C9A227,#E6C863)",
                }}
              >
                <CreditCardIcon
                  sx={{ fontSize: 35 }}
                />
              </Avatar>

              <Box>

                <Typography
                  variant="h4"
                  sx={{
                    color: "#1f2937",
                    fontWeight: 700,
                  }}
                >
                  Store Card Dashboard
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                  }}
                >
                  Professional prepaid card
                  management
                </Typography>

              </Box>

            </Box>

          </Grid>




          <Grid item xs={12} md={4}>

            <Grid container spacing={2}>

              <Grid item xs={6}>

                <Card
                  sx={{
                    p: 2,
                    borderRadius: "18px",
                  }}
                >

                  <Typography
                    sx={{
                      color: "#6b7280",
                    }}
                  >
                    Total Cards
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 30,
                      fontWeight: "bold",
                      color: "#C9A227",
                    }}
                  >
                    {totalCards}
                  </Typography>

                </Card>

              </Grid>

              <Grid item xs={6}>

                <Card
                  sx={{
                    p: 2,
                    borderRadius: "18px",
                  }}
                >

                  <Typography
                    sx={{
                      color: "#6b7280",
                    }}
                  >
                    Customers
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 30,
                      fontWeight: "bold",
                      color: "#C9A227",
                    }}
                  >
                    {customers.length}
                  </Typography>

                </Card>

              </Grid>

            </Grid>

          </Grid>

        </Grid>

      </Card>

            {/* ================= CARD PREVIEW ================= */}
              {previewCard && (
                <GeneratedCard
                  card={previewCard}
                  onClose={() => setPreviewCard(null)}
                />
              )}
            {/* ================= CARD PREVIEW END ================= */}

      {/* ========================= */}
      {/* CREATE CARD */}
      {/* ========================= */}

      <Card
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "24px",
          background: "#fff",
        }}
      >

        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mb={3}
        >

          <SecurityIcon
            sx={{
              color: "#C9A227",
            }}
          />

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
            }}
          >
            Create New Prepaid Card
          </Typography>

        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4}>

          <Grid item xs={12} md={6}>

            <Stack spacing={3}>

              <Autocomplete
                options={customers}
                value={selectedCustomer}
                onChange={(e, value) =>
                  setSelectedCustomer(value)
                }
                getOptionLabel={(option) =>
                  option.username || ""
                }
                onInputChange={(e, value) =>
                  setCustomerSearch(value)
                }
                renderOption={(
                  props,
                  option
                ) => (

                  <Box
                    component="li"
                    {...props}
                  >

                    <PersonIcon
                      sx={{
                        mr: 1,
                        color: "#C9A227",
                      }}
                    />

                    {option.username}

                  </Box>
                )}
                renderInput={(params) => (

                  <TextField
                    {...params}
                    label="Search Customer Username"
                  />
                )}
              />

              <TextField
                type="number"
                label="Initial Deposit Amount"
                value={amount}
                onChange={(e) =>
                  setAmount(e.target.value)
                }
              />

            </Stack>

          </Grid>

          <Grid item xs={12} md={6}>

            <Card
              sx={{
                p: 4,
                borderRadius: "20px",
                background:
                  "linear-gradient(135deg,#fff,#f9fafc)",
              }}
            >

              <Box
                display="flex"
                alignItems="center"
                gap={2}
              >

                <AccountBalanceWalletIcon
                  sx={{
                    fontSize: 45,
                    color: "#C9A227",
                  }}
                />

                <Box>

                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 22,
                    }}
                  >
                    Secure Store Card
                  </Typography>

                  <Typography
                    sx={{
                      color: "#6b7280",
                    }}
                  >
                    Create and manage
                    prepaid cards
                  </Typography>

                </Box>

              </Box>

              <Button
              fullWidth
              variant="contained"
              onClick={handleCreate}
              disabled={loading}
              startIcon={<AddCardIcon />}
              sx={{
                background:
                  "linear-gradient(135deg,#C9A227,#E6C863)",
              }}
            
              >

                {loading ? (

                  <CircularProgress
                    size={24}
                    sx={{
                      color: "#fff",
                    }}
                  />

                ) : (
                  "Create Prepaid Card"
                )}

              </Button>

            </Card>

          </Grid>

        </Grid>

      </Card>

      {/* ========================= */}
      {/* SEARCH */}
      {/* ========================= */}

      <Card
        sx={{
          mb: 3,
          p: 2,
          borderRadius: "18px",
        }}
      >

        <TextField
          fullWidth
          placeholder="Search card..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">

                <SearchIcon
                  sx={{
                    color: "#C9A227",
                  }}
                />

              </InputAdornment>
            ),
          }}
        />

      </Card>

      {/* ========================= */}
      {/* TABLE */}
      {/* ========================= */}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "24px",
        }}
      >

        <Table>

          <TableHead>

            <TableRow>

              {[
                "Account",
                "Customer",
                "PIN",
                "Balance",
                "Status",
                "Created By",
                "Created",
                "Deposit",
                "History",
                "Active",
              ].map((header) => (

                <TableCell
                  key={header}
                  sx={{
                    fontWeight: "bold",
                    color: "#C9A227",
                  }}
                >
                  {header}
                </TableCell>
              ))}

            </TableRow>

          </TableHead>

          <TableBody>

            {cards.map((card) => (

              <TableRow
                key={card.id}
                hover
              >

                <TableCell>
                  {card.account_number}
                </TableCell>

                <TableCell>
                  {card.customer_name}
                </TableCell>

                <TableCell>

                  <Button
                    size="small"
                    onClick={() =>
                      Swal.fire(
                        "Card PIN",
                        card.pin,
                        "info"
                      )
                    }
                    sx={{
                      color: "#C9A227",
                    }}
                  >
                    View PIN
                  </Button>

                </TableCell>

                <TableCell>

                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: "#16a34a",
                    }}
                  >
                    $
                    {Number(
                      card.balance
                    ).toLocaleString()}
                  </Typography>

                </TableCell>

                <TableCell>

                  <Chip
                    label={
                      card.is_active
                        ? "Active"
                        : "Inactive"
                    }
                    sx={{
                      background:
                        card.is_active
                          ? "#dcfce7"
                          : "#fee2e2",

                      color:
                        card.is_active
                          ? "#16a34a"
                          : "#dc2626",

                      fontWeight: "bold",
                    }}
                  />

                </TableCell>

                <TableCell>
                  {card.created_by_name ||
                    "System"}
                </TableCell>

                <TableCell>

                  {new Date(
                    card.created_at
                  ).toLocaleString()}

                </TableCell>

                <TableCell>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      handleDeposit(card)
                    }
                    sx={{
                      background:
                        "linear-gradient(135deg,#C9A227,#E6C863)",
                    }}
                  >
                    Deposit
                  </Button>

                </TableCell>

                <TableCell>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() =>
                      handleViewTransactions(
                        card
                      )
                    }
                    sx={{
                      color: "#C9A227",
                      borderColor:
                        "#C9A227",
                    }}
                  >
                    History
                  </Button>

                </TableCell>

                <TableCell>

                  <Switch
                    checked={card.is_active}
                    onChange={() =>
                      toggleActive(card)
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked":
                        {
                          color:
                            "#C9A227",
                        },

                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor:
                            "#C9A227",
                        },
                    }}
                  />

                </TableCell>

              </TableRow>

            ))}

          </TableBody>

        </Table>

      </TableContainer>

      {/* ========================= */}
      {/* PAGINATION */}
      {/* ========================= */}

      <Card
        sx={{
          mt: 3,
          borderRadius: "18px",
        }}
      >

        <TablePagination
          component="div"
          count={totalCards}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => {

            setPage(newPage);

            fetchCards(
              newPage + 1,
              search
            );
          }}
          onRowsPerPageChange={(e) => {

            setRowsPerPage(
              parseInt(
                e.target.value,
                10
              )
            );
          }}
          rowsPerPageOptions={[
            4,
            8,
            12,
            20,
          ]}
        />

      </Card>

    </Box>
  );
};

export default PrepaidCard;


